// SAD-186: TanStack Query bindings for the project closeout checklist.
// Modelled on activationGateHooks.ts — same NotFound-is-a-normal-state
// mapping, same invalidate-on-mutate shape.
import { Code, ConnectError } from '@connectrpc/connect';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { artifactClient, deliveryClient } from '../../../lib/connect/clients';
import { DOC_KEY } from '../Docs/hooks';
import { PROGRAM_KEY } from './hooks';
const CLOSEOUT_KEY = 'closeout-checklist';
const PERIOD_CLOSEOUT_KEY = 'period-closeout';
// A write targets the final closeout, or a contract period's checklist when
// periodId is set; refresh whichever it touched.
function invalidateCloseout(queryClient, programId, periodId) {
    queryClient.invalidateQueries({
        queryKey: periodId ? [
            PERIOD_CLOSEOUT_KEY,
            programId
        ] : [
            CLOSEOUT_KEY,
            programId
        ]
    });
}
export function useCloseoutChecklist(programId) {
    return useQuery({
        queryKey: [
            CLOSEOUT_KEY,
            programId
        ],
        enabled: !!programId,
        queryFn: async ()=>{
            // NotFound is a normal state, not an error: only StartCloseoutChecklist
            // seeds a checklist, so a program that hasn't started closeout yet has
            // none. ryugu answers CodeNotFound for those — map it to null so
            // consumers (nav, page) can branch without retries, same as
            // useActivationGate's GetActivationGate NotFound mapping.
            try {
                return await deliveryClient.getCloseoutChecklist({
                    programId
                });
            } catch (err) {
                if (err instanceof ConnectError && err.code === Code.NotFound) return null;
                throw err;
            }
        }
    });
}
export function useStartCloseoutChecklist() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (args)=>{
            return deliveryClient.startCloseoutChecklist({
                programId: args.programId,
                templateKey: args.templateKey ?? ''
            });
        },
        onSuccess: (_resp, vars)=>{
            queryClient.invalidateQueries({
                queryKey: [
                    CLOSEOUT_KEY,
                    vars.programId
                ]
            });
        },
        onError: (err)=>{
            toast.error(err instanceof Error ? err.message : 'Failed to start closeout checklist');
        }
    });
}
export function useAnswerCloseoutChecklistItem() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (args)=>{
            const resp = await deliveryClient.answerCloseoutChecklistItem({
                ...args,
                periodId: args.periodId ?? ''
            });
            return resp.item;
        },
        onSuccess: (_item, vars)=>{
            invalidateCloseout(queryClient, vars.programId, vars.periodId);
            toast.success('Checklist item saved');
        },
        onError: (err)=>{
            toast.error(err instanceof Error ? err.message : 'Failed to save checklist item');
        }
    });
}
export function useUpsertCloseoutOutstandingItem() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (args)=>{
            const resp = await deliveryClient.upsertCloseoutOutstandingItem({
                programId: args.programId,
                outstandingItem: args.outstandingItem,
                periodId: args.periodId ?? ''
            });
            return resp.outstandingItem;
        },
        onSuccess: (_item, vars)=>{
            invalidateCloseout(queryClient, vars.programId, vars.periodId);
        },
        onError: (err)=>{
            toast.error(err instanceof Error ? err.message : 'Failed to save outstanding item');
        }
    });
}
export function useDeleteCloseoutOutstandingItem() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (args)=>{
            await deliveryClient.deleteCloseoutOutstandingItem({
                ...args,
                periodId: args.periodId ?? ''
            });
        },
        onSuccess: (_void, vars)=>{
            invalidateCloseout(queryClient, vars.programId, vars.periodId);
        },
        onError: (err)=>{
            toast.error(err instanceof Error ? err.message : 'Failed to delete outstanding item');
        }
    });
}
/**
 * CompleteCloseoutChecklist mutation. The handler returns `checklist` on
 * success and `violations[]` (still-unresolved ordinals) on failure; callers
 * branch on the violations array so the failure path doesn't have to throw —
 * mirrors ActivateContractResponse / useActivateContract.
 */ export function useCompleteCloseoutChecklist() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (args)=>{
            return deliveryClient.completeCloseoutChecklist(args);
        },
        onSuccess: (resp, vars)=>{
            queryClient.invalidateQueries({
                queryKey: [
                    CLOSEOUT_KEY,
                    vars.programId
                ]
            });
            if (!resp.violations || resp.violations.length === 0) {
                toast.success('Closeout checklist completed');
            }
        },
        onError: (err)=>{
            toast.error(err instanceof Error ? err.message : 'Failed to complete closeout checklist');
        }
    });
}
/**
 * ExportCloseoutChecklist mutation — the explicit, re-runnable export action
 * (SAD-186 closeout export). Refused (FailedPrecondition) unless the
 * checklist is Completed; the resulting program document lands in the
 * program's Documents tab under the "Closeout" category.
 */ export function useExportCloseoutChecklist() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (args)=>{
            return deliveryClient.exportCloseoutChecklist(args);
        },
        onSuccess: (_resp, vars)=>{
            queryClient.invalidateQueries({
                queryKey: [
                    DOC_KEY,
                    'list',
                    vars.programId
                ]
            });
            toast.success('Closeout checklist exported to Documents');
        },
        onError: (err)=>{
            toast.error(err instanceof Error ? err.message : 'Failed to export closeout checklist');
        }
    });
}
/**
 * ExportCloseoutChecklist + a client-side download in one action (SAD-186
 * round 3, matt #8). Runs the same export that lands the artifact in the
 * program's Documents tab, then fetches the stored artifact and triggers a
 * browser download so a PM closing a program keeps a local copy instead of
 * having to dig it out of the archive later. The download reuses the artifact
 * service's presigned-URL + blob pattern (see ScoreAttachmentLink).
 */ export function useExportAndDownloadCloseout() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (args)=>{
            const resp = await deliveryClient.exportCloseoutChecklist(args);
            const artifactId = resp.document?.fileId;
            if (!artifactId) {
                throw new Error('export returned no downloadable document');
            }
            const [meta, dl] = await Promise.all([
                artifactClient.getArtifact({
                    artifactId
                }),
                artifactClient.generateDownloadUrl({
                    artifactId
                })
            ]);
            if (!dl.url) {
                throw new Error('no download URL returned');
            }
            const res = await fetch(dl.url);
            if (!res.ok) {
                throw new Error(`download failed: ${res.statusText}`);
            }
            const blobUrl = URL.createObjectURL(await res.blob());
            const anchor = document.createElement('a');
            anchor.href = blobUrl;
            anchor.download = meta.artifact?.filename || 'closeout-checklist.docx';
            document.body.appendChild(anchor);
            anchor.click();
            anchor.remove();
            // Revoke after the click kicks off the download; an immediate revoke can
            // race the browser's read of a large blob.
            setTimeout(()=>URL.revokeObjectURL(blobUrl), 0);
            return resp;
        },
        onSuccess: (_resp, vars)=>{
            queryClient.invalidateQueries({
                queryKey: [
                    DOC_KEY,
                    'list',
                    vars.programId
                ]
            });
            toast.success('Closeout checklist exported and downloaded');
        },
        onError: (err)=>{
            toast.error(err instanceof Error ? err.message : 'Failed to export and download checklist');
        }
    });
}
export function useCloseProgram() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (args)=>{
            return deliveryClient.closeProgram(args);
        },
        onSuccess: (_resp, vars)=>{
            queryClient.invalidateQueries({
                queryKey: [
                    CLOSEOUT_KEY,
                    vars.programId
                ]
            });
            queryClient.invalidateQueries({
                queryKey: [
                    PROGRAM_KEY,
                    'detail',
                    vars.programId
                ]
            });
            queryClient.invalidateQueries({
                queryKey: [
                    PROGRAM_KEY,
                    'list'
                ]
            });
            toast.success('Program closed');
        },
        onError: (err)=>{
            toast.error(err instanceof Error ? err.message : 'Failed to close program');
        }
    });
}
// ---------------------------------------------------------------------------
// Period closeout — contract periods only. The browser API has no way to name
// a team cycle, and nothing here asks for one.
// ---------------------------------------------------------------------------
/** Every contract period's checklist the program has started. */ export function usePeriodCloseouts(programId) {
    return useQuery({
        queryKey: [
            PERIOD_CLOSEOUT_KEY,
            programId,
            'list'
        ],
        enabled: !!programId,
        queryFn: async ()=>{
            const resp = await deliveryClient.listPeriodCloseouts({
                programId
            });
            return resp.checklists;
        }
    });
}
/** One period's checklist with its prefills and answer log; null when not started. */ export function usePeriodCloseoutChecklist(programId, periodId) {
    return useQuery({
        queryKey: [
            PERIOD_CLOSEOUT_KEY,
            programId,
            'detail',
            periodId
        ],
        enabled: !!programId && !!periodId,
        queryFn: async ()=>{
            try {
                return await deliveryClient.getPeriodCloseoutChecklist({
                    programId,
                    periodId
                });
            } catch (err) {
                if (err instanceof ConnectError && err.code === Code.NotFound) return null;
                throw err;
            }
        }
    });
}
export function useStartPeriodCloseoutChecklist() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (args)=>{
            return deliveryClient.startPeriodCloseoutChecklist({
                ...args,
                templateKey: ''
            });
        },
        onSuccess: (_resp, vars)=>invalidateCloseout(queryClient, vars.programId, vars.periodId),
        onError: (err)=>{
            toast.error(err instanceof Error ? err.message : 'Failed to start the period closeout');
        }
    });
}
/**
 * ClosePeriod mutation. Like Complete, the blocking items come back as
 * `violations` rather than as an error.
 */ export function useClosePeriod() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (args)=>{
            return deliveryClient.closePeriod(args);
        },
        onSuccess: (resp, vars)=>{
            invalidateCloseout(queryClient, vars.programId, vars.periodId);
            if (!resp.violations || resp.violations.length === 0) {
                toast.success('Period closed');
            }
        },
        onError: (err)=>{
            toast.error(err instanceof Error ? err.message : 'Failed to close the period');
        }
    });
}
